import { lockBodyScroll } from "../utils/scrollLock";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IoChevronDown, IoCheckmark } from "react-icons/io5";
import { useFocusTrap } from "../utils/focusTrap";

// A <select> replacement whose list is drawn by the app instead of the OS —
// the native popup on Android/iOS ignores the app's fonts, colors and dark
// mode entirely. Bottom sheet on phones, centered card on wider screens.
//
// options: [{ value, label }]. value/onChange work in strings, like a select.
// initialScrollValue: where a long list should open when nothing is picked
// yet (e.g. a typical birth year, instead of the top of a 100-year list).
export default function PickerSelect({
  id,
  label,
  placeholder,
  value,
  options,
  onChange,
  style,
  initialScrollValue
}) {
  const [open, setOpen] = useState(false);
  const listRef = useRef(null);
  const sheetRef = useRef(null);
  const selected = options.find(o => String(o.value) === String(value));
  useFocusTrap(sheetRef, open, {
    // Otherwise the trap defaults to the first option in DOM order, while
    // the scroll-to-selected effect below centers a different (already
    // chosen) one — on a long list (e.g. a birth-year picker) that leaves
    // keyboard focus on an option scrolled off-screen from what's visible.
    getInitialFocus: () => {
      const list = listRef.current;
      // == null, not a truthy check — a legitimately selected option value
      // of 0 is falsy and would otherwise skip this lookup, same reasoning
      // as the `??` used in the scroll-to-selected effect right below.
      if (!list || value == null) return null;
      return list.querySelector(`[data-value="${CSS.escape(String(value))}"]`);
    }
  });

  useEffect(() => {
    if (!open) return;
    const unlockScroll = lockBodyScroll();
    function onKeyDown(e) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      unlockScroll();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const list = listRef.current;
    // `value ?? initialScrollValue`, not `||` — a real, legitimately
    // selected option value of 0 is falsy and would otherwise fall through
    // to initialScrollValue (or an empty string) here even though the
    // "selected" checkmark elsewhere in this component correctly handles 0
    // (it compares with String(o.value) === String(value), not a truthiness
    // check) — so only the auto-scroll-into-view behavior was affected.
    const target = String(value ?? initialScrollValue ?? "");
    // CSS.escape avoids a value containing a `"` breaking out of the
    // attribute selector (and throwing inside this effect, uncaught).
    const el = target ? list.querySelector(`[data-value="${CSS.escape(target)}"]`) : null;
    if (el) list.scrollTop = el.offsetTop - list.clientHeight / 2 + el.clientHeight / 2;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <button
        type="button"
        id={id}
        className="virsa-input"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={selected ? `${label}: ${selected.label}` : label}
        style={{
          ...trigger,
          ...style,
          color: selected ? "var(--text-heading)" : "var(--text-muted)"
        }}
        onClick={() => setOpen(true)}
      >
        <span style={ellipsis}>{selected ? selected.label : placeholder}</span>
        <IoChevronDown style={{ flexShrink: 0, opacity: 0.6 }} />
      </button>

      {open &&
        createPortal(
          <div className="picker-backdrop" onClick={() => setOpen(false)}>
            <style>{css}</style>
            <div
              className="picker-sheet"
              ref={sheetRef}
              tabIndex={-1}
              role="dialog"
              aria-label={label}
              onClick={e => e.stopPropagation()}
            >
              <div className="picker-title">{label}</div>
              <div className="picker-list" role="listbox" ref={listRef}>
                {options.map(o => {
                  const isSelected = String(o.value) === String(value);
                  return (
                    <button
                      type="button"
                      key={o.value}
                      role="option"
                      aria-selected={isSelected}
                      data-value={o.value}
                      className="picker-option"
                      onClick={() => {
                        onChange(String(o.value));
                        setOpen(false);
                      }}
                    >
                      <span>{o.label}</span>
                      {isSelected && <IoCheckmark />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

const trigger = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 4,
  minWidth: 0,
  padding: "12px 10px",
  fontFamily: "inherit", // a button otherwise uses the phone's own font, not the app's
  fontSize: 14,
  fontWeight: 500,
  textAlign: "left",
  borderRadius: 10,
  border: "1.5px solid var(--border-input)",
  background: "var(--bg-surface)",
  outline: "none",
  cursor: "pointer"
};

const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const css = `
  .picker-backdrop {
    position: fixed;
    inset: 0;
    z-index: 2000;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: rgba(20, 14, 8, 0.5);
    animation: pickerFade 0.15s ease;
  }
  .picker-sheet {
    width: 100%;
    max-width: 440px;
    background: var(--bg-surface);
    border: 1px solid var(--border-medium);
    border-bottom: none;
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.25);
    padding-bottom: env(safe-area-inset-bottom);
    animation: pickerRise 0.2s ease;
  }
  .picker-title {
    font-family: 'Fraunces', serif;
    font-size: 18px;
    font-weight: 600;
    color: var(--text-heading);
    padding: 16px 20px 8px;
  }
  .picker-list {
    position: relative;
    max-height: 48vh;
    overflow-y: auto;
    padding: 4px 10px 14px;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }
  .picker-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 13px 14px;
    border: none;
    border-radius: 10px;
    background: none;
    color: var(--text-heading);
    font-size: 15px;
    font-weight: 500;
    text-align: left;
    cursor: pointer;
  }
  .picker-option:hover { background: var(--bg-soft); }
  .picker-option[aria-selected="true"] {
    background: var(--accent-primary);
    color: var(--accent-contrast);
    font-weight: 700;
  }
  @media (min-width: 641px) {
    .picker-backdrop { align-items: center; }
    .picker-sheet {
      max-width: 340px;
      border-bottom: 1px solid var(--border-medium);
      border-radius: 16px;
    }
  }
  @keyframes pickerFade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes pickerRise {
    from { opacity: 0; transform: translateY(24px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;
